/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';
import { useAtomValue } from 'jotai';
import { FormattedMessage } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { SectionName } from '../../../../common/analytics/section-name';
import { useSectionReveal } from '../../../../common/analytics/useSectionReveal';
import { useAtlassianUserContext } from '../../../../hooks/use-user-context';
import { CollaborationUsersAtom } from '../../../state';
import { SpacingWrapper } from '../../SpacingWrapper';

import blueBackground from './assets/blue-background.webp';
import blueMask from './assets/blue-mask.svg';
import greyBackground from './assets/grey-background.webp';
import greyMask from './assets/grey-mask.svg';
import limeBackground from './assets/lime-background.webp';
import limeMask from './assets/lime-mask.svg';
import purpleBackground from './assets/purple-background.webp';
import purpleMask from './assets/purple-mask.svg';
import yellowBackground from './assets/yellow-background.webp';
import yellowMask from './assets/yellow-mask.svg';
import { Collaborator } from './Collaborator';
import { messages } from './messages';

const RANK_CARDS = [
	{ backgroundColor: '#82B536', backgroundSrc: limeBackground, maskSrc: limeMask },
	{ backgroundColor: '#BF63F3', backgroundSrc: purpleBackground, maskSrc: purpleMask },
	{ backgroundColor: '#4D8CED', backgroundSrc: blueBackground, maskSrc: blueMask },
	{ backgroundColor: '#FCA700', backgroundSrc: yellowBackground, maskSrc: yellowMask },
	{ backgroundColor: '#8C8F97', backgroundSrc: greyBackground, maskSrc: greyMask },
] as const;

const styles = cssMap({
	spacingWrapper: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#111213',
	},
	container: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
		gap: token('space.400'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingTop: '100px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingBottom: '120px',
		'@media (max-width: 30rem)': {
			paddingTop: token('space.300'),
			paddingBottom: token('space.300'),
		},
	},
	title: {
		font: token('font.heading.large'),
		fontFamily: token('font.family.brand.heading'),
		// Subtle on-dark heading colour from the Figma design
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: token('color.text'),
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		letterSpacing: '0.5px',
		margin: 0,
		alignSelf: 'flex-start',
	},
	cardsRow: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'stretch',
		gap: token('space.300'),
		width: '100%',
		'@media (max-width: 30rem)': {
			flexDirection: 'column',
			alignItems: 'stretch',
		},
	},
});

export function CollaborationTop5Section(): JSX.Element | null {
	const { data: collaborators, error } = useAtomValue(CollaborationUsersAtom);
	const { identity: currentUser } = useAtlassianUserContext();
	const sectionRef = useSectionReveal<HTMLDivElement>(SectionName.CollaborationTop5);

	// TODO: GRAPHYTE-2509 Error fallback / Suspense Fallback
	if (error !== null) {
		return (
			<div>
				<FormattedMessage {...messages.loadCollaboratorsError} />
			</div>
		);
	}

	const rankedCollaborators = collaborators
		.filter((user) => user.id !== currentUser.id)
		.slice(0, RANK_CARDS.length);

	// TODO: GRAPHYTE-2516: no collaborators to show
	if (rankedCollaborators.length <= 2) {
		// eslint-disable-next-line no-console
		console.log('less than 3 collaborators to show');
		return null;
	}

	return (
		<SpacingWrapper css={styles.spacingWrapper}>
			<div ref={sectionRef} css={styles.container}>
				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
				<p css={styles.title}>
					<FormattedMessage {...messages.title} />
				</p>
				<div css={styles.cardsRow}>
					{rankedCollaborators.map((user, index) => (
						<Collaborator
							key={user.id}
							user={user}
							maskSrc={RANK_CARDS[index].maskSrc}
							backgroundSrc={RANK_CARDS[index].backgroundSrc}
							backgroundColor={RANK_CARDS[index].backgroundColor}
						/>
					))}
				</div>
			</div>
		</SpacingWrapper>
	);
}
